import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { StyledRouterLink } from "@evg-ui/lib/components/styles";
import { useTaskAnalytics } from "analytics";
import MetadataCard, {
  MetadataCardTitle,
  MetadataItem,
} from "components/MetadataCard";
import {
  TaskTimingMetric,
  getHoneycombTaskTimingURL,
} from "constants/externalResources/honeycomb";
import { TASK_TIMING_CONFIG_KEY } from "constants/index";
import { setObject } from "utils/localStorage";
import { createInitialState } from "./state";
import { TaskTimingConfigMenu } from "./TaskTimingConfig";

interface TaskTimingProps {
  buildVariant: string;
  projectIdentifier: string;
  taskName: string;
}

export const TaskTimingMetadata: React.FC<TaskTimingProps> = ({
  buildVariant,
  projectIdentifier,
  taskName,
}) => {
  const { sendEvent } = useTaskAnalytics();
  const [configState, setConfigState] = useState(createInitialState);

  useEffect(() => {
    // Update config in localStorage
    setObject(TASK_TIMING_CONFIG_KEY, configState);
  }, [configState]);

  const linkProps = (metric: TaskTimingMetric) => ({
    onClick: () =>
      sendEvent({
        name: "Clicked task timing link",
        metric,
        only_commits: configState.onlyCommits,
        only_successful: configState.onlySuccessful,
      }),
    to: getHoneycombTaskTimingURL({
      ...configState,
      buildVariant,
      metric,
      projectIdentifier,
      taskName,
    }),
  });

  return (
    <MetadataCard
      title={
        <CardHeader>
          <MetadataCardTitle>Historical Task Timing</MetadataCardTitle>
          <TaskTimingConfigMenu
            configState={configState}
            setConfigState={setConfigState}
          />
        </CardHeader>
      }
    >
      <MetadataItem label="Total time">
        <StyledRouterLink {...linkProps(TaskTimingMetric.TotalTime)}>
          Activated &rarr; Finish
        </StyledRouterLink>
      </MetadataItem>
      <MetadataItem label="Wait time">
        <StyledRouterLink {...linkProps(TaskTimingMetric.WaitTime)}>
          Activated &rarr; Start
        </StyledRouterLink>
      </MetadataItem>
      <MetadataItem label="Run time">
        <StyledRouterLink {...linkProps(TaskTimingMetric.RunTime)}>
          Start &rarr; Finish
        </StyledRouterLink>
      </MetadataItem>
    </MetadataCard>
  );
};

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;
